import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Link as LinkIcon, Mail, FileText, Search, User, MapPin, Calendar, Hash, TrendingUp, Building, DollarSign, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatNumberWithCommas } from "@/lib/utils";

interface EnrichedData {
  domain?: string;
  company_name?: string;
  linkedin_url?: string;
  email_domain?: string;
  industry?: string;
  headquarters?: string;
  foundation_year?: string;
  founded_year?: string;
  company_description?: string;
  employees_estimate?: string;
  revenue_estimate?: string;
  person_position?: string;
  person_linkedin?: string;
  data_quality_score?: number;
  bizraport_url?: string;
  bizraport_data?: {
    krs?: string;
    nip?: string;
    regon?: string;
    company_name?: string;
    address?: string;
    registration_date?: string;
    financial_summary?: string;
    legal_form?: string;
    ownership_form?: string;
    registry_court?: string;
    pkd_codes?: string[];
    wojewodztwo?: string;
    share_capital?: string;
    year_established?: string;
  };
  bizraport_validation?: {
    status?: string;
    nip_format_valid?: boolean;
    krs_format_valid?: boolean;
    company_name_match?: boolean;
    data_sources_used?: string[];
    validation_notes?: string;
  };
  debug_info?: {
    linkedin_person_found?: boolean;
    linkedin_person_score?: number;
    phantombuster_enabled?: boolean;
    linkedin_company_found?: boolean;
    linkedin_company_score?: number;
    person_linkedin_method?: string;
    top_company_candidates?: any[];
    company_linkedin_method?: string;
    linkedin_person_ai_used?: boolean;
    linkedin_company_ai_used?: boolean;
    person_data_quality_score?: number | null;
    phantombuster_person_used?: boolean;
    company_data_quality_score?: number | null;
    linkedin_person_candidates?: number;
    phantombuster_company_used?: boolean;
    linkedin_company_candidates?: number;
  };
  linkedin_profile_data?: {
    fullName?: string;
    headline?: string;
    location?: string;
    connections?: string;
    emails?: string[];
    skills?: string[];
  };
  linkedin_company_data?: {
    name?: string;
    description?: string;
    industry?: string;
    size?: string;
    headquarters?: string;
    founded?: string;
    followerCount?: string;
    specialties?: string[];
  };
  alternative_sources?: {
    linkedin_company?: string;
    rejestr_io?: string;
    golden_com?: string;
  };
  company_news?: Array<{
    title: string;
    link: string;
    source?: string;
    date?: string;
  }>;
  person_data?: {
    name?: string;
    headline?: string;
    location?: string;
    linkedin_url?: string;
    experience?: any[];
    education?: any[];
  };
}

interface LeadEnrichedDataViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrichedData: EnrichedData | null;
  domain: string;
}

export function LeadEnrichedDataViewer({ 
  open, 
  onOpenChange, 
  enrichedData,
  domain 
}: LeadEnrichedDataViewerProps) {
  if (!enrichedData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dane wzbogacone - {domain}</DialogTitle>
          </DialogHeader>
          <div className="text-center py-8 text-muted-foreground">
            Brak danych wzbogaconych
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const normalizedData = (enrichedData as any)?.enrichedData || enrichedData || {};
  const companyData = normalizedData;
  const bizData = normalizedData.bizraport_data;
  const debugInfo = normalizedData.debug_info;
  const personData = normalizedData.person_data || normalizedData.linkedin_profile_data;
  const companyLinkedInData = normalizedData.linkedin_company_data;
  const bizValidation = normalizedData.bizraport_validation;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Dane wzbogacone - {domain}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Data Quality Score */}
          {companyData.data_quality_score !== undefined && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Jakość danych:</span>
                  <span className="font-bold">{companyData.data_quality_score}/100</span>
                </div>
                <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                  <div 
                    className={`h-full transition-all ${
                      companyData.data_quality_score >= 70 ? 'bg-green-500' :
                      companyData.data_quality_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${companyData.data_quality_score}%` }}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Company Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Podstawowe dane firmy
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {companyData.company_name && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Nazwa firmy:</span>
                  <p className="text-base font-semibold">{companyData.company_name}</p>
                </div>
              )}

              {companyData.company_description && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Opis firmy:</span>
                  <p className="text-sm text-muted-foreground mt-1">{companyData.company_description}</p>
                </div>
              )}
              
              {companyData.email_domain && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{companyData.email_domain}</span>
                </div>
              )}
              
              {companyData.linkedin_url && (
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(companyData.linkedin_url, '_blank')}
                    className="w-full justify-start"
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Profil LinkedIn firmy
                  </Button>
                </div>
              )}

              {companyData.industry && (
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{companyData.industry}</span>
                </div>
              )}

              {companyData.headquarters && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{companyData.headquarters}</span>
                </div>
              )}

              {(companyData.foundation_year || companyData.founded_year) && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Rok założenia: {companyData.foundation_year || companyData.founded_year}</span>
                </div>
              )}

              {companyData.employees_estimate && (
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Pracownicy: {companyData.employees_estimate}</span>
                </div>
              )}

              {companyData.revenue_estimate && (
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Przychody: {companyData.revenue_estimate}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Company News */}
          {companyData.company_news && Array.isArray(companyData.company_news) && companyData.company_news.length > 0 && (
            <Card className="bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  📰 Newsy o firmie
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {companyData.company_news.map((news, idx) => (
                    <li key={idx} className="text-sm">
                      <a href={news.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                        {news.title}
                      </a>
                      {news.source && <span className="text-muted-foreground ml-2">({news.source})</span>}
                      {news.date && <span className="text-muted-foreground ml-2 text-xs">{news.date}</span>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Bizraport Validation */}
          {bizValidation && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Walidacja Bizraport
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Status:</span>
                  <Badge variant={
                    bizValidation.status === 'verified' ? 'default' :
                    bizValidation.status === 'partial' ? 'secondary' : 'destructive'
                  }>
                    {bizValidation.status?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                </div>
                {bizValidation.nip_format_valid !== undefined && (
                  <div className="text-sm">
                    NIP Format: {bizValidation.nip_format_valid ? '✓ Valid' : '✗ Invalid'}
                  </div>
                )}
                {bizValidation.krs_format_valid !== undefined && (
                  <div className="text-sm">
                    KRS Format: {bizValidation.krs_format_valid ? '✓ Valid' : '✗ Invalid'}
                  </div>
                )}
                {bizValidation.company_name_match !== undefined && (
                  <div className="text-sm">
                    Company Name Match: {bizValidation.company_name_match ? '✓ Match' : '✗ No Match'}
                  </div>
                )}
                {bizValidation.data_sources_used && (
                  <div className="text-sm">
                    <span className="font-medium">Sources:</span> {bizValidation.data_sources_used.join(', ')}
                  </div>
                )}
                {bizValidation.validation_notes && (
                  <div className="text-sm text-muted-foreground mt-2">
                    {bizValidation.validation_notes}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Bizraport Data */}
          {bizData && (
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Dane rejestrowe (Bizraport)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {companyData.bizraport_url && (
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(companyData.bizraport_url, '_blank')}
                      className="w-full justify-start"
                    >
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Profil Bizraport.pl
                    </Button>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {bizData.krs && (
                    <div className="bg-white/60 dark:bg-gray-900/60 p-3 rounded">
                      <div className="text-xs text-muted-foreground">KRS:</div>
                      <p className="text-sm font-mono font-semibold">{bizData.krs}</p>
                    </div>
                  )}
                  
                  {bizData.nip && (
                    <div className="bg-white/60 dark:bg-gray-900/60 p-3 rounded">
                      <div className="text-xs text-muted-foreground">NIP:</div>
                      <p className="text-sm font-mono font-semibold">{bizData.nip}</p>
                    </div>
                  )}
                  
                  {bizData.regon && (
                    <div className="bg-white/60 dark:bg-gray-900/60 p-3 rounded">
                      <div className="text-xs text-muted-foreground">REGON:</div>
                      <p className="text-sm font-mono font-semibold">{bizData.regon}</p>
                    </div>
                  )}
                  
                  {bizData.share_capital && (
                    <div className="bg-white/60 dark:bg-gray-900/60 p-3 rounded">
                      <div className="text-xs text-muted-foreground">Kapitał zakładowy:</div>
                      <p className="text-sm font-semibold">{bizData.share_capital}</p>
                    </div>
                  )}

                  {bizData.registration_date && (
                    <div className="bg-white/60 dark:bg-gray-900/60 p-3 rounded">
                      <div className="text-xs text-muted-foreground">Data rejestracji:</div>
                      <p className="text-sm">{bizData.registration_date}</p>
                    </div>
                  )}

                  {(bizData.year_established || bizData.registration_date) && (
                    <div className="bg-white/60 dark:bg-gray-900/60 p-3 rounded">
                      <div className="text-xs text-muted-foreground">Rok założenia:</div>
                      <p className="text-sm">{bizData.year_established || bizData.registration_date}</p>
                    </div>
                  )}
                </div>

                {bizData.address && (
                  <div className="flex items-start gap-2 bg-white/60 dark:bg-gray-900/60 p-3 rounded">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span className="text-sm">{bizData.address}</span>
                  </div>
                )}

                {(bizData.legal_form || bizData.ownership_form) && (
                  <div className="space-y-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                    {bizData.legal_form && (
                      <div className="bg-white/60 dark:bg-gray-900/60 p-2 rounded">
                        <span className="text-xs text-muted-foreground">Forma prawna:</span>
                        <p className="text-sm">{bizData.legal_form}</p>
                      </div>
                    )}
                    {bizData.ownership_form && (
                      <div className="bg-white/60 dark:bg-gray-900/60 p-2 rounded">
                        <span className="text-xs text-muted-foreground">Forma własności:</span>
                        <p className="text-sm">{bizData.ownership_form}</p>
                      </div>
                    )}
                  </div>
                )}

                {bizData.financial_summary && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 p-4 rounded-lg border-2 border-green-200 dark:border-green-800 mt-3">
                    <div className="font-bold text-green-900 dark:text-green-100 mb-3 flex items-center gap-2">
                      <span className="text-xl">💰</span>
                      <span>Podsumowanie finansowe</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {bizData.financial_summary.split('; ').map((item: string, idx: number) => {
                        const [label, value] = item.split(': ');
                        const formattedValue = formatNumberWithCommas(value);
                        return (
                          <div key={idx} className="bg-white/70 dark:bg-gray-900/70 p-2 rounded">
                            <div className="text-xs text-green-700 dark:text-green-300 font-semibold">{label}</div>
                            <div className="text-base font-bold text-green-900 dark:text-green-100">{formattedValue}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {bizData.pkd_codes && bizData.pkd_codes.length > 0 && (
                  <div className="pt-2 border-t border-blue-200 dark:border-blue-800">
                    <span className="text-xs text-muted-foreground mb-1 block">Kody PKD:</span>
                    <div className="flex flex-wrap gap-1">
                      {bizData.pkd_codes.slice(0, 5).map((code, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {code}
                        </Badge>
                      ))}
                      {bizData.pkd_codes.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{bizData.pkd_codes.length - 5} więcej
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Person Data */}
          {personData && personData.fullName && (
            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 border-blue-200 dark:border-blue-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Dane osoby kontaktowej (LinkedIn)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Imię i nazwisko:</span>
                  <p className="text-base font-semibold">{personData.fullName}</p>
                </div>

                {personData.headline && (
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Stanowisko:</span>
                    <p className="text-sm">{personData.headline}</p>
                  </div>
                )}

                {personData.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{personData.location}</span>
                  </div>
                )}

                {personData.connections && (
                  <div className="text-sm">
                    <span className="font-medium">Połączenia:</span> {personData.connections}
                  </div>
                )}

                {personData.emails && personData.emails.length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium">Emails:</span> {personData.emails.join(', ')}
                  </div>
                )}

                {personData.skills && personData.skills.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-muted-foreground mb-1 block">Umiejętności:</span>
                    <div className="flex flex-wrap gap-1">
                      {personData.skills.slice(0, 10).map((skill, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Alternative Person Data */}
          {(companyData.person_position || companyData.person_linkedin) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Dodatkowe dane osoby
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {companyData.person_position && (
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Stanowisko:</span>
                    <p className="text-sm">{companyData.person_position}</p>
                  </div>
                )}
                {companyData.person_linkedin && (
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(companyData.person_linkedin, '_blank')}
                      className="w-full justify-start"
                    >
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Profil LinkedIn osoby
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* LinkedIn Company Data */}
          {companyLinkedInData && (
            <Card className="bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-950 dark:to-teal-950 border-green-200 dark:border-green-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Dane firmy z LinkedIn
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {companyLinkedInData.name && (
                  <div><strong>Nazwa:</strong> {companyLinkedInData.name}</div>
                )}
                {companyLinkedInData.description && (
                  <div><strong>Opis:</strong> {companyLinkedInData.description}</div>
                )}
                {companyLinkedInData.industry && (
                  <div><strong>Branża:</strong> {companyLinkedInData.industry}</div>
                )}
                {companyLinkedInData.size && (
                  <div><strong>Wielkość:</strong> {companyLinkedInData.size}</div>
                )}
                {companyLinkedInData.headquarters && (
                  <div><strong>Siedziba:</strong> {companyLinkedInData.headquarters}</div>
                )}
                {companyLinkedInData.founded && (
                  <div><strong>Rok założenia:</strong> {companyLinkedInData.founded}</div>
                )}
                {companyLinkedInData.followerCount && (
                  <div><strong>Obserwujący:</strong> {companyLinkedInData.followerCount}</div>
                )}
                {companyLinkedInData.specialties && companyLinkedInData.specialties.length > 0 && (
                  <div><strong>Specjalizacje:</strong> {companyLinkedInData.specialties.join(', ')}</div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Alternative Sources */}
          {companyData.alternative_sources && Object.keys(companyData.alternative_sources).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" />
                  Alternatywne źródła
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {companyData.alternative_sources.linkedin_company && (
                  <div className="text-sm">
                    <strong>LinkedIn:</strong>{' '}
                    <a href={companyData.alternative_sources.linkedin_company} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {companyData.alternative_sources.linkedin_company}
                    </a>
                  </div>
                )}
                {companyData.alternative_sources.rejestr_io && (
                  <div className="text-sm">
                    <strong>Rejestr.io:</strong>{' '}
                    <a href={companyData.alternative_sources.rejestr_io} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {companyData.alternative_sources.rejestr_io}
                    </a>
                  </div>
                )}
                {companyData.alternative_sources.golden_com && (
                  <div className="text-sm">
                    <strong>Golden.com:</strong>{' '}
                    <a href={companyData.alternative_sources.golden_com} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {companyData.alternative_sources.golden_com}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* PhantomBuster Status */}
          {debugInfo && debugInfo.phantombuster_enabled && (
            <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950 dark:to-indigo-950 border-purple-200 dark:border-purple-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Status PhantomBuster
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant={debugInfo.phantombuster_person_used ? "default" : "outline"}>
                    Person: {debugInfo.phantombuster_person_used ? '✓ Used' : '✗ Not used'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Method: {debugInfo.person_linkedin_method || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={debugInfo.phantombuster_company_used ? "default" : "outline"}>
                    Company: {debugInfo.phantombuster_company_used ? '✓ Used' : '✗ Not used'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Method: {debugInfo.company_linkedin_method || 'N/A'}
                  </span>
                </div>
                {(debugInfo.person_data_quality_score || debugInfo.company_data_quality_score) && (
                  <div className="mt-2 pt-2 border-t border-purple-200 dark:border-purple-800">
                    <div className="text-sm font-semibold">Data Quality Scores:</div>
                    {debugInfo.person_data_quality_score && (
                      <div className="text-sm">
                        Person: {debugInfo.person_data_quality_score}/100
                      </div>
                    )}
                    {debugInfo.company_data_quality_score && (
                      <div className="text-sm">
                        Company: {debugInfo.company_data_quality_score}/100
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Debug Info */}
          {debugInfo && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Informacje techniczne
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Company Search Info */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Wyszukiwanie firmy:</h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant={debugInfo.linkedin_company_found ? "default" : "secondary"}>
                      {debugInfo.linkedin_company_found ? "✓" : "✗"} LinkedIn znaleziony
                    </Badge>
                    {debugInfo.linkedin_company_score !== undefined && (
                      <Badge variant="outline">
                        Score: {debugInfo.linkedin_company_score}
                      </Badge>
                    )}
                    {debugInfo.company_linkedin_method && (
                      <Badge variant="outline">
                        Metoda: {debugInfo.company_linkedin_method}
                      </Badge>
                    )}
                    {debugInfo.linkedin_company_candidates !== undefined && (
                      <Badge variant="outline">
                        Kandydatów: {debugInfo.linkedin_company_candidates}
                      </Badge>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Person Search Info */}
                {debugInfo.linkedin_person_found !== undefined && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Wyszukiwanie osoby:</h4>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={debugInfo.linkedin_person_found ? "default" : "secondary"}>
                        {debugInfo.linkedin_person_found ? "✓" : "✗"} Osoba znaleziona
                      </Badge>
                      {debugInfo.linkedin_person_score !== undefined && (
                        <Badge variant="outline">
                          Score: {debugInfo.linkedin_person_score}
                        </Badge>
                      )}
                      {debugInfo.person_linkedin_method && (
                        <Badge variant="outline">
                          Metoda: {debugInfo.person_linkedin_method}
                        </Badge>
                      )}
                      {debugInfo.linkedin_person_candidates !== undefined && (
                        <Badge variant="outline">
                          Kandydatów: {debugInfo.linkedin_person_candidates}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Additional Debug Info */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Dodatkowe:</h4>
                  <div className="flex flex-wrap gap-2">
                    {debugInfo.linkedin_company_ai_used && (
                      <Badge variant="outline">
                        AI użyte (firma)
                      </Badge>
                    )}
                    {debugInfo.linkedin_person_ai_used && (
                      <Badge variant="outline">
                        AI użyte (osoba)
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
